import React, { useEffect } from 'react'
import { Button } from '../ui/button'
import { useRTL } from '../../contexts/RTLContext'
import { useFloating } from '../../contexts/FloatingContext'
import { useInteraction } from '../../contexts/InteractionContext'
import { getTextAlignment } from '../../utils/rtl'
import { useSuggestions } from '../../hooks'
import { useMobile } from '../../utils/mobile'
import { motion, AnimatePresence } from 'motion/react'

interface PromptSuggestionsProps {
  content?: {
    title?: string
    content?: string
    url?: string
  }
  onSuggestionClick: (suggestion: string) => void
  className?: string
  isFocused?: boolean
  position?: 'above' | 'below'
}

export const PromptSuggestions = ({ 
  content, 
  onSuggestionClick, 
  className, 
  isFocused = true,
  position = 'below'
}: PromptSuggestionsProps) => {
  const { isRTL, language } = useRTL()
  const { isFloating } = useFloating()
  const { setSuggestionsVisible } = useInteraction()
  const isMobile = useMobile()
  
  // Use the suggestions hook
  const { 
    data: suggestionsData, 
    isLoading, 
    error 
  } = useSuggestions({
    context: content ? {
      articleTitle: content.title,
      articleContent: content.content,
      articleUrl: content.url
    } : undefined,
    language,
    enabled: !!(content?.title || content?.content)
  })

  const suggestions = suggestionsData?.suggestions || []

  const handleSuggestionMouseDown = (
    e: React.MouseEvent, 
    suggestion: string
  ) => {
    // Prevent the blur event from firing on the input
    e.preventDefault()
    e.stopPropagation() // Also stop propagation to prevent bubble up
    
    // Handle the suggestion selection
    onSuggestionClick(suggestion)
  }

  // Get RTL-aware classes
  const textAlignment = getTextAlignment(isRTL)
  const justifyContent = isRTL ? 'justify-end' : 'justify-start'
  
  // Determine spacing based on position
  const spacingClass = position === 'above' ? 'mb-3' : 'mt-3'
  
  // Determine if suggestions should be visible
  // const shouldShow = isMobile ? isFocused : true
  const shouldShow = isFocused
  
  // Track suggestions visibility in the interaction context
  useEffect(() => {
    const hasVisibleSuggestions = shouldShow && suggestions.length > 0
    setSuggestionsVisible(hasVisibleSuggestions)
    
    return () => setSuggestionsVisible(false)
  }, [shouldShow, suggestions.length, setSuggestionsVisible])

  if (isLoading && shouldShow) {
    return (
      <motion.div 
        className={`flex flex-col gap-2 ${spacingClass} ${className}`}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
      >
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-10 bg-gray-100 rounded-xl animate-pulse"
          />
        ))}
      </motion.div>
    )
  }

  if (error && suggestions.length === 0) {
    return null // Don't show anything if there's an error and no suggestions
  }

  if (suggestions.length === 0) {
    return null // Don't show anything if no suggestions
  }

  // Component now uses simple height calculations

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div 
          className={`flex flex-col gap-2 ${spacingClass} ${className}`} 
          dir={isRTL ? 'rtl' : 'ltr'}
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.95 }}
          transition={{ 
            duration: 0.2,
            ease: "easeOut"
          }}
        >
          {suggestions.map((suggestion: string, index: number) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                delay: index * 0.05,
                duration: 0.15 
              }}
            >
                                 <Button
                     variant="ghost"
                size="sm"
                className={`w-full h-auto py-3 px-4 rounded-xl hover:bg-accent hover:text-accent-foreground transition-colors text-sm text-muted-foreground whitespace-normal ${textAlignment}`}
                onMouseDown={(e) => handleSuggestionMouseDown(e, suggestion)}
                dir={isRTL ? 'rtl' : 'ltr'}
              >
                <span style={{ width: '100%', textAlign: isRTL ? 'right' : 'left' }}>
                  {suggestion}
                </span>
              </Button>
            </motion.div>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  )
} 